import * as React from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { NotConfiguredBanner } from "@/components/common/NotConfiguredBanner";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  UploadCloud,
  FileSpreadsheet,
  CheckCircle2,
  Eye,
  ShieldCheck,
  AlertTriangle,
  AlertCircle,
  Loader2,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/supabase/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { createBrowserSupabase } from "@/supabase/client";
import { parseNebimProductSalesExcel } from "@/features/nebim/parse";
import { parseNebimStockExcel } from "@/features/nebim/parseStock";
import {
  commitNebimBatch,
  commitNebimStockBatch,
  findDuplicateBatch,
  findUnmatchedSkus,
} from "@/features/nebim/service";
import type {
  CommitResult,
  ParseResult,
} from "@/features/nebim/types";
import type { StockParseResult } from "@/features/nebim/parseStock";
import { auditCompanyDataAfterChange } from "@/lib/ai";

type ConfiguredSupabase = Extract<ReturnType<typeof createBrowserSupabase>, { ok: true }>["client"];

type Phase = "idle" | "parsing" | "preview" | "duplicate" | "committing" | "done" | "error";

type DuplicateInfo = {
  batch_id: string;
  file_name: string;
  created_at: string;
  status: string;
};

const steps = [
  { id: 1, label: "Yükle", icon: UploadCloud, matches: ["idle", "parsing", "preview", "duplicate", "committing", "done"] as Phase[] },
  { id: 2, label: "Doğrula", icon: ShieldCheck, matches: ["parsing", "preview", "duplicate", "committing", "done"] as Phase[] },
  { id: 3, label: "Önizle", icon: Eye, matches: ["preview", "duplicate", "committing", "done"] as Phase[] },
  { id: 4, label: "Onayla", icon: CheckCircle2, matches: ["committing", "done"] as Phase[] },
];

const NebimImport: React.FC = () => {
  const { user, companyId } = useAuth();
  const { toast } = useToast();
  const factory = React.useMemo(() => createBrowserSupabase(), []);
  const canUse = factory.ok && !!user && !!companyId;

  const [phase, setPhase] = React.useState<Phase>("idle");
  const [parseResult, setParseResult] = React.useState<ParseResult | null>(null);
  const [salesReportDate, setSalesReportDate] = React.useState("");
  const [duplicate, setDuplicate] = React.useState<DuplicateInfo | null>(null);
  const [commitError, setCommitError] = React.useState<string | null>(null);
  const [commitResult, setCommitResult] = React.useState<Extract<CommitResult, { ok: true }> | null>(null);
  const [unmatchedSkus, setUnmatchedSkus] = React.useState<string[]>([]);
  const [productsChecked, setProductsChecked] = React.useState(true);
  const [productsError, setProductsError] = React.useState<string | null>(null);

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const stockFileInputRef = React.useRef<HTMLInputElement>(null);
  const [stockParseResult, setStockParseResult] = React.useState<StockParseResult | null>(null);
  const [stockRowFilter, setStockRowFilter] = React.useState<"all" | "ok" | "warn" | "error">("all");
  const [stockPage, setStockPage] = React.useState(1);
  const [stockPageSize, setStockPageSize] = React.useState(50);
  const [stockSnapshotDate, setStockSnapshotDate] = React.useState("");
  const [stockBusy, setStockBusy] = React.useState(false);
  const [stockSaving, setStockSaving] = React.useState(false);
  const [stockError, setStockError] = React.useState<string | null>(null);
  const [stockCommitResult, setStockCommitResult] = React.useState<Extract<CommitResult, { ok: true }> | null>(null);
  const [stockCatalogMessage, setStockCatalogMessage] = React.useState<string | null>(null);
  const [catalogBuildingId, setCatalogBuildingId] = React.useState<string | null>(null);
  const [catalogProgress, setCatalogProgress] = React.useState<string | null>(null);
  const [catalogProgressBatchId, setCatalogProgressBatchId] = React.useState<string | null>(null);
  const [historyRows, setHistoryRows] = React.useState<Array<{
    id: string; original_filename: string | null; status: string;
    source_system: string | null; import_type: string | null;
    total_rows: number | null; valid_rows: number | null; error_rows: number | null;
    created_at: string;
  }>>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [historyError, setHistoryError] = React.useState<string | null>(null);
  const historyRequestId = React.useRef(0);
  const activeCompanyIdRef = React.useRef(companyId);
  const salesRequestId = React.useRef(0);
  const stockParseRequestId = React.useRef(0);
  const stockCommitRequestId = React.useRef(0);
  const salesCommitRequestId = React.useRef(0);

  React.useLayoutEffect(() => {
    activeCompanyIdRef.current = companyId;
    // Invalidate and clear company-scoped history immediately, before paint.
    // The passive effect below will load history for the newly selected company.
    historyRequestId.current += 1;
    setHistoryRows([]);
    setHistoryError(null);
    setHistoryLoading(false);
    salesRequestId.current += 1;
    stockParseRequestId.current += 1;
    stockCommitRequestId.current += 1;
    salesCommitRequestId.current += 1;
    setPhase("idle");
    setParseResult(null);
    setDuplicate(null);
    setCommitError(null);
    setCommitResult(null);
    setUnmatchedSkus([]);
    setProductsError(null);
    setProductsChecked(true);
    setStockParseResult(null);
    setStockSnapshotDate("");
    setStockError(null);
    setStockCommitResult(null);
    setStockCatalogMessage(null);
    setStockBusy(false);
    setStockSaving(false);
    // Catalog work and its status are scoped to the selected company.
    setCatalogBuildingId(null);
    setCatalogProgress(null);
    setCatalogProgressBatchId(null);
  }, [companyId]);

  const loadHistory = React.useCallback(async () => {
    if (!factory.ok || !companyId) return;
    const requestId = ++historyRequestId.current;
    setHistoryLoading(true);
    setHistoryError(null);
    const pageSize = 500;
    const maxRows = 10000;
    const allRows: Array<{
      id: string; original_filename: string | null; status: string;
      source_system: string | null; import_type: string | null;
      total_rows: number | null; valid_rows: number | null; error_rows: number | null;
      created_at: string;
    }> = [];
    let offset = 0;
    let loadError: string | null = null;
    let exceededLimit = false;

    while (true) {
      const { data, error } = await factory.client
        .from("import_batches")
        .select("id, original_filename, status, source_system, import_type, total_rows, valid_rows, error_rows, created_at")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false })
        .range(offset, offset + pageSize - 1);

      // A company/session change or unmount invalidates older in-flight requests.
      if (requestId !== historyRequestId.current) return;
      if (error) {
        loadError = error.message;
        break;
      }

      const page = data ?? [];
      allRows.push(...page);
      if (page.length < pageSize) break;

      offset += page.length;
      if (allRows.length >= maxRows) {
        // Distinguish exactly 10,000 rows from a truncated history.
        const { data: extra, error: extraError } = await factory.client
          .from("import_batches")
          .select("id")
          .eq("company_id", companyId)
          .order("created_at", { ascending: false })
          .range(maxRows, maxRows);
        if (requestId !== historyRequestId.current) return;
        if (extraError) loadError = extraError.message;
        else if ((extra ?? []).length > 0) exceededLimit = true;
        break;
      }
    }

    if (loadError || exceededLimit) {
      // Never show a partial history as though it were complete.
      setHistoryRows([]);
      const message = loadError
        ? `Geçmiş kayıtları tam yüklenemedi: ${loadError}`
        : "Aktarım geçmişi 10.000 kaydı aşıyor. Eksiksiz listeyi göstermek için sayfalı ekran gerekir.";
      setHistoryError(message);
      toast({ title: "Geçmiş kayıtları yüklenemedi", description: message, variant: "destructive" });
    } else {
      setHistoryRows(allRows);
      setHistoryError(null);
    }
    setHistoryLoading(false);
  }, [factory, companyId, toast]);

  React.useEffect(() => {
    void loadHistory();
    return () => {
      historyRequestId.current += 1;
    };
  }, [loadHistory]);

  const resetAll = () => {
    setPhase("idle");
    setParseResult(null);
    setDuplicate(null);
    setCommitError(null);
    setCommitResult(null);
    setUnmatchedSkus([]);
    setProductsError(null);
    setProductsChecked(true);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  async function handleFile(file: File) {
    if (!canUse || !factory.ok || !companyId) return;
    const requestedCompanyId = companyId;
    const requestId = ++salesRequestId.current;

    setParseResult(null);
    setDuplicate(null);
    setCommitResult(null);
    setUnmatchedSkus([]);
    setProductsError(null);
    setProductsChecked(true);
    setCommitError(null);

    setPhase("parsing");

    try {
      const result = await parseNebimProductSalesExcel(file);
      if (requestId !== salesRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
      setParseResult(result);
      // Source date is not upload time; require manual date if metadata is absent.
      setSalesReportDate(result.source_period_start && result.source_period_start === result.source_period_end ? result.source_period_start : "");

      // Duplicate check
      const dupResp = await findDuplicateBatch(
        factory.client,
        requestedCompanyId,
        result.file_hash,
      );
      if (requestId !== salesRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
      // Duplicate protection is a safety gate: if its query fails, do not
      // proceed to approval because we cannot guarantee the file is unique.
      if (dupResp.error) {
        throw new Error(`Mükerrer dosya kontrolü yapılamadı; güvenlik için aktarım durduruldu: ${dupResp.error.message}`);
      }
      if (dupResp.data) {
        setDuplicate({
          batch_id: dupResp.data.id,
          file_name: dupResp.data.original_filename,
          created_at: dupResp.data.created_at,
          status: dupResp.data.status,
        });
        setPhase("duplicate");
        return;
      }

      // Products matching (best-effort)
      const skus = result.rows
        .map((r) => r.parsed.sku)
        .filter((s): s is string => !!s);
      const match = await findUnmatchedSkus(
        factory.client,
        requestedCompanyId,
        skus,
      );
      if (requestId !== salesRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
      setUnmatchedSkus(match.unmatched);
      setProductsChecked(match.checked);
      setProductsError(match.error ?? null);

      setPhase("preview");
    } catch (e) {
      if (requestId !== salesRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
      setCommitError(
        e instanceof Error ? e.message : "Dosya okunamadı veya hatalı biçim.",
      );
      setPhase("error");
    }
  }

  async function handleStockFile(file: File) {
    if (!canUse || !factory.ok || !companyId) return;
    const requestedCompanyId = companyId;
    const requestId = ++stockParseRequestId.current;
    setStockBusy(true);
    setStockError(null);
    setStockCommitResult(null);
    setStockCatalogMessage(null);
    setStockParseResult(null);
    try {
      const result = await parseNebimStockExcel(file);
      if (requestId !== stockParseRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
      setStockParseResult(result);
      setStockRowFilter("all");
      setStockPage(1);
      if (result.missing_required.length > 0) {
        setStockError(`Stok dosyasının gerekli sütunları bulunamadı: ${result.missing_required.join(", ")}`);
      }
    } catch (e) {
      if (requestId === stockParseRequestId.current && activeCompanyIdRef.current === requestedCompanyId) {
        setStockError(e instanceof Error ? e.message : "Stok dosyası okunamadı.");
      }
    } finally {
      if (requestId === stockParseRequestId.current && activeCompanyIdRef.current === requestedCompanyId) setStockBusy(false);
      if (stockFileInputRef.current) stockFileInputRef.current.value = "";
    }
  }

  async function runCatalogInChunks(client: ConfiguredSupabase, batchId: string, requestedCompanyId: string, onProgress?: (message: string) => void) {
    const totals = { products: 0, variants: 0, mappings: 0 };
    const phases = ["products", "variants", "mappings"] as const;
    for (const phaseName of phases) {
      let offset = 0;
      while (true) {
        const { data, error } = await client.rpc("build_nebim_product_catalog_chunk", {
          p_company_id: requestedCompanyId, p_batch_id: batchId, p_phase: phaseName, p_offset: offset, p_limit: 100,
        });
        if (activeCompanyIdRef.current !== requestedCompanyId) throw new Error("Şirket değişti; işlem güvenlik için durduruldu.");
        if (error) throw new Error(error.message);
        const result = data as { processed?: number; done?: boolean } | null;
        const processed = Number(result?.processed ?? 0);
        totals[phaseName] += processed;
        onProgress?.(phaseName + " aşaması: " + (offset + processed).toLocaleString("tr-TR") + " kayıt işlendi");
        if (result?.done === true || processed < 100 || processed === 0) break;
        offset += processed;
      }
    }
    return totals;
  }

  async function handleStockCommit() {
    if (!canUse || !factory.ok || !stockParseResult || !user || !companyId) return;
    if (!stockSnapshotDate) {
      setStockError("Stok Excel dosyasının gerçek görüntü tarihini seçmeden kaydedemezsiniz. Dosyada güvenilir bir stok tarihi yok.");
      return;
    }
    const requestedCompanyId = companyId;
    const requestId = ++stockCommitRequestId.current;
    setStockSaving(true);
    setStockError(null);
    try {
      const duplicate = await findDuplicateBatch(factory.client, requestedCompanyId, stockParseResult.file_hash);
      if (requestId !== stockCommitRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
      if (duplicate.error) {
        setStockError(`Mükerrer dosya kontrolü yapılamadı; güvenlik için aktarım durduruldu: ${duplicate.error.message}`);
        return;
      }
      if (duplicate.data) {
        setStockError(`Bu dosya daha önce içe aktarılmış (${duplicate.data.status}). Aynı dosya ikinci kez kaydedilmedi.`);
        return;
      }
      const result = await commitNebimStockBatch(factory.client, requestedCompanyId, user.id, stockParseResult, stockSnapshotDate);
      if (requestId !== stockCommitRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
      if (!result.ok) {
        setStockError(`[${result.stage}] ${result.message}`);
        return;
      }
      setStockCommitResult(result);
      const canonical = result.canonicalization;
      const canonicalMessage = canonical?.materialized
        ? `Kanonik stok görüntüsü ${stockSnapshotDate} tarihiyle kaydedildi (${canonical.canonical_rows ?? result.row_count} satır). Kaynak depo etiketi: ${stockParseResult.warehouses.join(", ") || "bilinmiyor"}. ${canonical.location_mapping_required ? "Depo etiketi için tam mağaza eşleştirmesi bekleniyor; stok bakiyesi onaylanmadı." : "Depo etiketi mevcut mağaza koduyla tam eşleşti; görüntü hâlâ staging/onay aşamasında."}`
        : `Staging kaydı tamamlandı ancak kanonik stok görüntüsü işlenmedi: ${canonical?.message ?? "İşleme sonucu doğrulanamadı."}`;
      setStockCatalogMessage(canonicalMessage + " Ürün kataloğu küçük paketlerle hazırlanıyor…");
      try {
        const catalog = await runCatalogInChunks(factory.client, result.batch_id, requestedCompanyId, (progress) => setStockCatalogMessage(canonicalMessage + " " + progress));
        if (requestId !== stockCommitRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
        setStockCatalogMessage(canonicalMessage + " Otomatik katalog hazır: " + catalog.products.toLocaleString("tr-TR") + " ürün kodu, " + catalog.variants.toLocaleString("tr-TR") + " renk/beden kimliği ve " + catalog.mappings.toLocaleString("tr-TR") + " eşleştirme kimliği işlendi. Belirsiz maliyetler ve stok bakiyesi değiştirilmedi.");
      } catch (catalogError) {
        if (requestId !== stockCommitRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
        setStockCatalogMessage(canonicalMessage + " Katalog tamamlanamadı: " + (catalogError instanceof Error ? catalogError.message : "Beklenmeyen hata."));
      }
      // Run after stock catalog preparation so the audit sees the newest catalog.
      void auditCompanyDataAfterChange(requestedCompanyId, "Nebim stok aktarımı").then((audit) => {
        if (activeCompanyIdRef.current !== requestedCompanyId) return;
        toast({
          title: audit.ok ? "Yapay zekâ stok denetimi tamamlandı" : "Stok kaydedildi; AI denetimi tamamlanamadı",
          description: audit.ok ? audit.text.slice(0, 900) : audit.message,
          variant: audit.ok ? "default" : "destructive",
        });
      }).catch((auditError) => {
        if (activeCompanyIdRef.current === requestedCompanyId) toast({ title: "AI stok denetimi başlatılamadı", description: auditError instanceof Error ? auditError.message : "Beklenmeyen hata.", variant: "destructive" });
      });
      void loadHistory();
    } catch (e) {
      if (requestId === stockCommitRequestId.current && activeCompanyIdRef.current === requestedCompanyId) {
        setStockError(e instanceof Error ? e.message : "Stok dosyası kaydedilirken beklenmeyen hata oluştu.");
      }
    } finally {
      if (requestId === stockCommitRequestId.current && activeCompanyIdRef.current === requestedCompanyId) setStockSaving(false);
    }
  }

  async function handleBuildCatalogForBatch(batchId: string) {
    if (!canUse || !factory.ok || !companyId || !user || catalogBuildingId) return;
    const requestedCompanyId = companyId;
    setCatalogBuildingId(batchId);
    setCatalogProgressBatchId(batchId);
    setCatalogProgress("Katalog hazırlanıyor: başlangıç");
    try {
      const catalog = await runCatalogInChunks(factory.client, batchId, requestedCompanyId, setCatalogProgress);
      if (activeCompanyIdRef.current !== requestedCompanyId) return;
      setCatalogProgress("Katalog tamamlandı: " + catalog.products.toLocaleString("tr-TR") + " ürün, " + catalog.variants.toLocaleString("tr-TR") + " varyant, " + catalog.mappings.toLocaleString("tr-TR") + " eşleştirme işlendi.");
      toast({
        title: "Otomatik ürün kataloğu hazır",
        description: catalog.products.toLocaleString("tr-TR") + " ürün kodu, " + catalog.variants.toLocaleString("tr-TR") + " renk/beden kimliği ve " + catalog.mappings.toLocaleString("tr-TR") + " eşleştirme kimliği işlendi. Stok bakiyesi ve maliyetler değiştirilmedi.",
      });
    } catch (e) {
      // Ignore stale results from a request started for a previously selected company.
      if (activeCompanyIdRef.current === requestedCompanyId) {
        setCatalogProgress("Hata: " + (e instanceof Error ? e.message : "Beklenmeyen hata."));
        toast({
          title: "Katalog oluşturulamadı",
          description: e instanceof Error ? e.message : "Beklenmeyen hata.",
          variant: "destructive",
        });
      }
    } finally {
      if (activeCompanyIdRef.current === requestedCompanyId) {
        setCatalogBuildingId(null);
        void loadHistory();
      }
    }
  }

  async function handleCommit() {
    if (!canUse || !factory.ok || !parseResult || !companyId || !user) return;
    const requestedCompanyId = companyId;
    const requestId = ++salesCommitRequestId.current;
    setPhase("committing");
    setCommitError(null);
    try {
      if (!salesReportDate) {
        setCommitError("Satış raporunun gerçek tarihini seçmeden kaydedemezsiniz. Yükleme tarihi rapor tarihi olarak kullanılmayacak.");
        setPhase("preview");
        return;
      }
      const datedParseResult: ParseResult = { ...parseResult, source_period_start: salesReportDate, source_period_end: salesReportDate };
      const res = await commitNebimBatch(factory.client, requestedCompanyId, user.id, datedParseResult);
      if (requestId !== salesCommitRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
      if (!res.ok) {
        setCommitError(`[${res.stage}] ${res.message}`);
        setPhase("error");
        return;
      }
      setCommitResult(res);
      setPhase("done");
      // Read-only AI consistency audit after a successful import.
      void auditCompanyDataAfterChange(requestedCompanyId, "Nebim satış aktarımı").then((audit) => {
        if (activeCompanyIdRef.current !== requestedCompanyId) return;
        toast({
          title: audit.ok ? "Yapay zekâ veri denetimi tamamlandı" : "Aktarım kaydedildi; AI denetimi tamamlanamadı",
          description: audit.ok ? audit.text.slice(0, 900) : audit.message,
          variant: audit.ok ? "default" : "destructive",
        });
      }).catch((auditError) => {
        if (activeCompanyIdRef.current === requestedCompanyId) toast({ title: "AI denetimi başlatılamadı", description: auditError instanceof Error ? auditError.message : "Beklenmeyen hata.", variant: "destructive" });
      });
      // The newly committed batch should appear in history without a page reload.
      void loadHistory();
    } catch (e) {
      if (requestId !== salesCommitRequestId.current || activeCompanyIdRef.current !== requestedCompanyId) return;
      setCommitError(e instanceof Error ? e.message : "Beklenmeyen bir aktarım hatası oluştu.");
      setPhase("error");
    }
  }

  const filteredStockRows = stockParseResult
    ? stockParseResult.rows.filter((row) => stockRowFilter === "all" || row.status === stockRowFilter)
    : [];
  const stockPageCount = Math.max(1, Math.ceil(filteredStockRows.length / stockPageSize));
  const safeStockPage = Math.min(stockPage, stockPageCount);
  const visibleStockRows = filteredStockRows.slice((safeStockPage - 1) * stockPageSize, safeStockPage * stockPageSize);

  return (
    <div data-testid="page-nebim-import" className="space-y-8">
      <PageHeader
        eyebrow="Veri İşleme"
        title="Nebim Excel İçe Aktarma"
        description="Nebim satış dosyasını yükle, doğrula, önizle ve onayla. Hiçbir satır sessizce atlanmaz."
      />

      {!canUse ? (
        <NotConfiguredBanner
          title="Bu bölüm için şirket üyeliği gerekli"
          description="Oturumunuz açık değil veya şirket üyeliği yüklenmedi."
        />
      ) : null}

      {/* Adım göstergesi */}
      <section
        aria-label="İçe aktarma adımları"
        className="rounded-lg border border-border bg-card p-5"
      >
        <ol className="flex flex-wrap items-center gap-3 md:gap-6">
          {steps.map((step, idx) => {
            const active = (step.matches as string[]).includes(phase);
            return (
              <li
                key={step.id}
                data-testid={`import-step-${step.id}`}
                className="flex items-center gap-3"
              >
                <div
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-full border",
                    active
                      ? "border-accent/50 bg-accent/10 text-accent"
                      : "border-border bg-background text-muted-foreground",
                  )}
                >
                  <step.icon className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    Adım {step.id}
                  </div>
                  <div className="text-sm font-medium text-foreground">
                    {step.label}
                  </div>
                </div>
                {idx < steps.length - 1 ? (
                  <span className="hidden h-px w-10 bg-border md:block" />
                ) : null}
              </li>
            );
          })}
        </ol>
      </section>

      <Tabs defaultValue="sales">
        <div className="overflow-x-auto">
          <TabsList data-testid="nebim-tabs">
            <TabsTrigger value="sales" data-testid="nebim-tab-sales">
              Satış Dosyası
            </TabsTrigger>
            <TabsTrigger value="stock" data-testid="nebim-tab-stock">
              Stok Dosyası
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="nebim-tab-history">
              Geçmiş İçe Aktarmalar
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="sales" className="mt-5 space-y-5">
          {phase === "idle" && (
            <UploadBox
              disabled={!canUse}
              onPick={() => fileInputRef.current?.click()}
            />
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            data-testid="nebim-file-input"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const input = e.currentTarget;
              const f = input.files?.[0];
              input.value = "";
              if (f) void handleFile(f);
            }}
          />

          {phase === "parsing" && (
            <div
              data-testid="nebim-parsing"
              className="flex items-center gap-3 rounded-lg border border-border bg-card p-6"
            >
              <Loader2 className="h-5 w-5 animate-spin text-accent" />
              <span className="text-sm text-foreground">
                Dosya okunuyor ve doğrulanıyor…
              </span>
            </div>
          )}

          {phase === "duplicate" && duplicate && parseResult && (
            <DuplicateCard
              duplicate={duplicate}
              onCancel={resetAll}
            />
          )}

          {phase === "preview" && parseResult && (
            <PreviewCard
              parseResult={parseResult}
              reportDate={salesReportDate}
              onReportDateChange={setSalesReportDate}
              unmatchedSkus={unmatchedSkus}
              productsChecked={productsChecked}
              productsError={productsError}
              onConfirm={handleCommit}
              onCancel={resetAll}
            />
          )}

          {phase === "committing" && (
            <div
              data-testid="nebim-committing"
              className="flex items-center gap-3 rounded-lg border border-border bg-card p-6"
            >
              <Loader2 className="h-5 w-5 animate-spin text-accent" />
              <span className="text-sm text-foreground">
                Satırlar Supabase'e yazılıyor…
              </span>
            </div>
          )}

          {phase === "done" && commitResult && (
            <SuccessCard result={commitResult} onReset={resetAll} />
          )}

          {phase === "error" && (
            <ErrorCard message={commitError} onReset={resetAll} />
          )}
        </TabsContent>

        <TabsContent value="stock" className="mt-5 space-y-5">
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
            <strong>Stok görüntüsü güvenli şekilde hazırlanır:</strong> Kayıt, kaynak tarih ve depo etiketiyle kanonik stok görüntüsüne işlenir. Depo etiketi mevcut mağaza koduyla tam eşleşmiyorsa mağazaya bağlanmaz ve onaylanmış stok bakiyesi olarak kullanılmaz.
          </div>
          <section className="rounded-lg border border-border bg-card p-5 space-y-4">
            <div>
              <h3 className="font-serif text-xl text-foreground">Nebim stok anlık görüntüsü</h3>
              <p className="text-sm text-muted-foreground">Depo, ürün kodu, barkod, renk/beden, envanter ve kullanılabilir miktar alanlarını okur.</p>
            </div>
            <div className="max-w-sm space-y-1">
              <label htmlFor="nebim-stock-snapshot-date" className="text-sm font-medium text-foreground">Stok görüntüsünün tarihi</label>
              <input
                id="nebim-stock-snapshot-date"
                data-testid="nebim-stock-snapshot-date"
                type="date"
                value={stockSnapshotDate}
                onChange={(event) => setStockSnapshotDate(event.target.value)}
                disabled={stockSaving}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                required
              />
              <p className="text-xs text-muted-foreground">Excel dosyasında stok tarihi bulunmadığı için gerçek rapor tarihini seçmelisin. Bugünün tarihi otomatik varsayılmaz. Eksi stok miktarları (ör. poşet gibi sarf malzemeleri) hata değildir ve tek başına aktarımı engellemez.</p>
            </div>
            <input
              ref={stockFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              data-testid="nebim-stock-file-input"
              onChange={(event) => {
                const input = event.currentTarget;
                const file = input.files?.[0];
                input.value = "";
                if (file) void handleStockFile(file);
              }}
            />
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => stockFileInputRef.current?.click()} disabled={!canUse || stockBusy || stockSaving}>
                <FileSpreadsheet className="mr-2 h-4 w-4" /> Stok Excel dosyası seç
              </Button>
              {stockParseResult ? (
                <Button variant="outline" onClick={() => { setStockParseResult(null); setStockCommitResult(null); setStockError(null); setStockSnapshotDate(""); }}>
                  Dosyayı temizle
                </Button>
              ) : null}
            </div>
            {stockBusy ? <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Stok dosyası okunuyor…</div> : null}
            {stockError ? <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{stockError}</div> : null}
            {stockParseResult ? (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" aria-label="Stok satırlarını duruma göre filtrele">
                  {([
                    { key: "all", label: "Toplam satır", value: stockParseResult.row_count, testId: "stock-count-total" },
                    { key: "ok", label: "Geçerli", value: stockParseResult.valid_count, testId: "stock-count-valid" },
                    { key: "warn", label: "Uyarılı", value: stockParseResult.warn_count, testId: "stock-count-warn" },
                    { key: "error", label: "Hatalı", value: stockParseResult.error_count, testId: "stock-count-error" },
                  ] as const).map((item) => (
                    <button key={item.key} type="button" data-testid={item.testId} aria-pressed={stockRowFilter === item.key}
                      onClick={() => { setStockRowFilter(item.key); setStockPage(1); }}
                      className={cn("rounded-lg border bg-card p-4 text-left transition-colors hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", stockRowFilter === item.key ? "border-accent ring-1 ring-accent/40" : "border-border")}>
                      <span className="block text-[11px] uppercase tracking-wider text-muted-foreground">{item.label}</span>
                      <span className={cn("num mt-1 block text-2xl font-500", item.key === "ok" ? "text-foreground" : item.key === "warn" ? "text-accent" : item.key === "error" ? "text-destructive" : "text-foreground")}>{item.value}</span>
                      <span className="mt-1 block text-[11px] text-muted-foreground">{stockRowFilter === item.key ? "Seçili filtre" : "Satırları göster"}</span>
                    </button>
                  ))}
                </div>
                <div className="rounded-md border border-border p-3 text-xs text-muted-foreground">
                  <div>Dosya: <span className="font-mono text-foreground">{stockParseResult.file_name}</span></div>
                  <div>Depo(lar): <span className="text-foreground">{stockParseResult.warehouses.join(", ") || "bulunamadı"}</span></div>
                  <div>SHA-256: <span className="font-mono text-foreground">{stockParseResult.file_hash.slice(0, 16)}…</span></div>
                </div>
                {stockParseResult.missing_required.length > 0 ? (
                  <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
                    Zorunlu sütunlar eksik: {stockParseResult.missing_required.join(", ")}
                  </div>
                ) : null}
                {!stockSnapshotDate ? (
                  <div role="status" data-testid="stock-date-required" className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-foreground">
                    <strong>Aktarım şu anda tarih seçilmediği için bekliyor.</strong> Yukarıdaki “Stok görüntüsünün tarihi” alanından Excel raporunun gerçek tarihini seç. Negatif stoklar engel değildir.
                  </div>
                ) : null}
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-secondary/50 text-muted-foreground">
                      <tr><th className="px-3 py-2">Satır</th><th className="px-3 py-2">Durum / hata</th><th className="px-3 py-2">Depo</th><th className="px-3 py-2">Ürün kodu</th><th className="px-3 py-2">Barkod</th><th className="px-3 py-2">Ürün</th><th className="px-3 py-2">Renk / Beden</th><th className="px-3 py-2 text-right">Envanter</th><th className="px-3 py-2 text-right">Kullanılabilir</th></tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {visibleStockRows.map((row) => (
                        <tr key={row.row_index} className={row.status === "error" ? "bg-destructive/5" : row.status === "warn" ? "bg-amber-500/5" : undefined}>
                          <td className="px-3 py-2">{row.row_index}</td>
                          <td className="px-3 py-2">
                            <span className={cn("rounded-full border px-2 py-0.5 text-[10px] uppercase", row.status === "error" ? "border-destructive/40 bg-destructive/10 text-destructive" : row.status === "warn" ? "border-amber-500/40 bg-amber-500/10 text-amber-800" : "border-border text-muted-foreground")}>{row.status === "error" ? "Hatalı" : row.status === "warn" ? "Uyarılı" : "Geçerli"}</span>
                            {row.issues.length > 0 ? <ul className="mt-1 space-y-1 text-[11px] text-muted-foreground">{row.issues.map((issue, index) => <li key={`${issue.code}-${index}`}>{issue.message}</li>)}</ul> : null}
                          </td>
                          <td className="px-3 py-2">{row.parsed.store_name ?? "—"}</td>
                          <td className="px-3 py-2 font-mono">{row.parsed.sku ?? "—"}</td>
                          <td className="px-3 py-2 font-mono">{row.parsed.barcode ?? "—"}</td>
                          <td className="px-3 py-2">{row.parsed.product_name ?? "—"}</td>
                          <td className="px-3 py-2">{[row.parsed.color_name, row.parsed.size].filter(Boolean).join(" / ") || "—"}</td>
                          <td className="px-3 py-2 text-right">{formatNumber(row.parsed.inventory_qty)}</td>
                          <td className="px-3 py-2 text-right">{formatNumber(row.parsed.available_qty)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-3">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Filtreye uyan {filteredStockRows.length.toLocaleString("tr-TR")} satır gösteriliyor; toplam {stockParseResult.row_count.toLocaleString("tr-TR")} satır doğrulandı.</p>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <label htmlFor="stock-preview-page-size" className="text-xs text-muted-foreground">Sayfa başına</label>
                      <select id="stock-preview-page-size" data-testid="stock-preview-page-size" value={stockPageSize} onChange={(event) => { setStockPageSize(Number(event.target.value)); setStockPage(1); }} className="rounded-md border border-border bg-background px-2 py-1 text-sm"><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option><option value={200}>200</option></select>
                      <span className="text-xs text-muted-foreground">Sayfa {safeStockPage} / {stockPageCount}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setStockPage(1)} disabled={safeStockPage <= 1}>İlk</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setStockPage((page) => Math.max(1, page - 1))} disabled={safeStockPage <= 1}>Önceki</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setStockPage((page) => Math.min(stockPageCount, page + 1))} disabled={safeStockPage >= stockPageCount}>Sonraki</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setStockPage(stockPageCount)} disabled={safeStockPage >= stockPageCount}>Son</Button>
                  </div>
                  <Button onClick={() => void handleStockCommit()} disabled={stockSaving || stockParseResult.missing_required.length > 0 || !stockSnapshotDate || !canUse}>
                    {stockSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                    Stok görüntüsünü işle
                  </Button>
                </div>
              </>
            ) : null}
            {stockCommitResult ? (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-foreground">
                <strong>{stockCommitResult.canonicalization?.materialized ? "Stok görüntüsü işlendi." : "Staging kaydı tamamlandı."}</strong> {stockCommitResult.row_count.toLocaleString("tr-TR")} satır işlendi. Stok görüntüsü otomatik olarak onaylanmaz; kaynak depo etiketi eşleştirilmeden mağaza bakiyesi oluşturulmuş sayılmaz.
                {stockCatalogMessage ? <p className="mt-2">{stockCatalogMessage}</p> : null}
              </div>
            ) : null}
          </section>
        </TabsContent>

        <TabsContent value="history" className="mt-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-serif text-xl text-foreground">İçe aktarma geçmişi</h3>
              <p className="text-sm text-muted-foreground">Şirketin son 100 içe aktarma kaydı; farklı kaynak etiketleri ve başarısız denemeler dâhildir.</p>
            </div>
            <Button variant="outline" onClick={() => void loadHistory()} disabled={historyLoading}>
              {historyLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Yenile
            </Button>
          </div>
          {historyError ? (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              Geçmiş yüklenemedi: {historyError}
            </div>
          ) : null}
          {historyLoading && historyRows.length === 0 ? (
            <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">Kayıtlar yükleniyor…</div>
          ) : historyRows.length === 0 && !historyError ? (
            <EmptyState icon={FileSpreadsheet} title="Henüz içe aktarma kaydı yok" description="Yeni bir Nebim dosyası onaylandığında veya bir deneme başarısız olduğunda kayıt burada görünür." testId="nebim-history-empty" />
          ) : historyRows.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-border bg-card">
              <table className="w-full text-left text-sm">
                <thead className="bg-secondary/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Tarih</th><th className="px-4 py-3">Dosya</th>
                    <th className="px-4 py-3">Kaynak / Tür</th><th className="px-4 py-3">Durum</th><th className="px-4 py-3 text-right">Toplam</th>
                    <th className="px-4 py-3 text-right">Geçerli</th><th className="px-4 py-3 text-right">Hatalı</th><th className="px-4 py-3">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {historyRows.map((row) => (
                    <tr key={row.id}>
                      <td className="whitespace-nowrap px-4 py-3">{new Date(row.created_at).toLocaleString("tr-TR")}</td>
                      <td className="px-4 py-3 font-mono text-xs">
                      {row.original_filename ?? "Dosya adı yok"}
                      
                    </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{row.source_system ?? "kaynak yok"}{row.import_type ? ` / ${row.import_type}` : ""}</td>
                      <td className="px-4 py-3"><span className={cn("rounded-full border px-2 py-1 text-xs", row.status === "committed" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700" : row.status === "failed" ? "border-destructive/40 bg-destructive/10 text-destructive" : "border-border text-muted-foreground")}>{formatBatchStatus(row.status)}</span></td>
                      <td className="px-4 py-3 text-right tabular-nums">{row.total_rows ?? "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{row.valid_rows ?? "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{row.error_rows ?? "—"}</td>
                      <td className="px-4 py-3">
                        {row.status === "committed" && row.import_type === "nebim_stock_snapshot" ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void handleBuildCatalogForBatch(row.id)}
                              disabled={!canUse || !!catalogBuildingId}
                              data-testid={`build-catalog-${row.id}`}
                            >
                              {catalogBuildingId === row.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                              {catalogBuildingId === row.id ? "Katalog hazırlanıyor" : "Kataloğu oluştur"}
                            </Button>
                            {catalogProgressBatchId === row.id && catalogProgress ? (
                              <div role="status" aria-live="polite" className="mt-1 max-w-48 text-xs text-muted-foreground">
                                {catalogProgress}
                              </div>
                            ) : null}
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NebimImport;

// --- Alt bileşenler ---

const UploadBox: React.FC<{ disabled: boolean; onPick: () => void }> = ({
  disabled,
  onPick,
}) => (
  <div
    data-testid="nebim-sales-upload"
    className="relative flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card/60 px-6 py-14 text-center"
  >
    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
      <UploadCloud className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
    </div>
    <h3 className="font-serif text-xl text-foreground">
      Nebim ürün bazlı satış raporunu yükleyin
    </h3>
    <p className="mt-1.5 max-w-md text-sm text-muted-foreground">
      Beklenen 18 sütun: Ürün Kodu, Ürün Adı, Renk Kodu, Renk Açıklaması,
      Barkod, BEDEN, Brüt Satış Miktarı, İade Miktar, Satış Miktarı, Brüt
      Satış (VH/VD), Satış İadesi (VH/VD), Satış (VH/VD), Brüt/İade/Toplam
      İskontoları. Başlık satırı otomatik bulunur; boş ve toplam satırları
      atlanır.
    </p>
    <Button
      variant="outline"
      disabled={disabled}
      className="mt-5"
      onClick={onPick}
      data-testid="nebim-pick-file-button"
    >
      Dosya seç
    </Button>
    <p className="mt-3 text-[11px] text-muted-foreground">
      Kabul edilen biçimler: .xlsx, .xls — tamamen istemci tarafında parse edilir;
      dosya içeriği onay vermediğiniz sürece Supabase'e yazılmaz.
    </p>
  </div>
);

const DuplicateCard: React.FC<{
  duplicate: DuplicateInfo;
  onCancel: () => void;
}> = ({ duplicate, onCancel }) => (
  <div
    data-testid="nebim-duplicate"
    className="space-y-4 rounded-lg border border-accent/40 bg-accent/5 p-5"
  >
    <div className="flex items-start gap-3">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
      <div className="space-y-1">
        <h3 className="font-serif text-xl text-foreground">
          Bu dosya daha önce içe aktarılmış
        </h3>
        <p className="text-sm text-muted-foreground">
          Aynı SHA-256 hash'ine sahip bir batch bulundu. Mükerrer yazmayı
          önlemek için işlem iptal edildi.
        </p>
      </div>
    </div>
    <dl className="grid grid-cols-1 gap-3 rounded-md border border-border bg-card p-4 text-sm sm:grid-cols-3">
      <div>
        <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Dosya adı
        </dt>
        <dd className="font-mono text-xs text-foreground">
          {duplicate.file_name}
        </dd>
      </div>
      <div>
        <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Durum
        </dt>
        <dd className="text-foreground">{duplicate.status}</dd>
      </div>
      <div>
        <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Tarih
        </dt>
        <dd className="text-foreground">
          {new Date(duplicate.created_at).toLocaleString("tr-TR")}
        </dd>
      </div>
    </dl>
    <Button variant="outline" onClick={onCancel} data-testid="nebim-duplicate-cancel">
      Tamam, farklı dosya seç
    </Button>
  </div>
);

const PreviewCard: React.FC<{
  parseResult: ParseResult;
  reportDate: string;
  onReportDateChange: (value: string) => void;
  unmatchedSkus: string[];
  productsChecked: boolean;
  productsError: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ parseResult, reportDate, onReportDateChange, unmatchedSkus, productsChecked, productsError, onConfirm, onCancel }) => {
  const previewRows = parseResult.rows.slice(0, 10);
  const issueGroups = aggregateIssues(parseResult);
  const validSalesRows = parseResult.rows.filter((row) => row.status !== "error");
  const hasVatIncludedSource = Object.values(parseResult.header_map).includes("net_sales_vd");
  const previewNetSalesExVat = validSalesRows.reduce((sum, row) => sum + (row.parsed.net_sales_vh ?? 0), 0);
  const previewNetSalesIncVat = validSalesRows.reduce((sum, row) => sum + (row.parsed.net_sales_vd ?? 0), 0);
  const previewMoney = (value: number) => new Intl.NumberFormat("tr-TR", {
    style: "currency", currency: "TRY", minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(value);

  return (
    <div data-testid="nebim-preview" className="space-y-5">
      {/* Sayaçlar */}
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Counter label="Toplam satır" value={parseResult.row_count} testId="count-total" />
        <Counter
          label="Geçerli"
          value={parseResult.valid_count}
          tone="success"
          testId="count-valid"
        />
        <Counter
          label="Uyarılı"
          value={parseResult.warn_count}
          tone="warn"
          testId="count-warn"
        />
        <Counter
          label="Hatalı"
          value={parseResult.error_count}
          tone="error"
          testId="count-error"
        />
      </section>

      <section data-testid="nebim-sales-totals-preview" className="rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold text-foreground">Aktarılacak satış toplamları</h3>
          {hasVatIncludedSource
            ? <span className="rounded-full border border-emerald-600/30 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800">Nebim KDV dâhil (VD) alanı bulundu</span>
            : <span className="rounded-full border border-amber-600/30 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800">VD sütunu yok · KDV oranı ile hesaplama gerekebilir</span>}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-md bg-secondary/40 p-3">
            <div className="text-xs text-muted-foreground">Net satış · KDV hariç (VH)</div>
            <div className="num mt-1 text-xl font-semibold">{previewMoney(previewNetSalesExVat)}</div>
          </div>
          <div className="rounded-md bg-secondary/40 p-3">
            <div className="text-xs text-muted-foreground">Net satış · KDV dâhil (VD)</div>
            <div className="num mt-1 text-xl font-semibold">{hasVatIncludedSource ? previewMoney(previewNetSalesIncVat) : "Kaynakta yok"}</div>
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Toplamlar yalnızca hatalı olmayan ürün satırlarından, Excel'in net satış (VH/VD) sütunları doğrudan toplanarak hesaplanır. KDV dâhil tutar yeniden KDV eklenerek üretilmez.</p>
      </section>

      <section className="rounded-lg border border-amber-400/50 bg-amber-50 p-4 text-sm text-amber-950" data-testid="sales-report-date-section">
        <label htmlFor="nebim-sales-report-date" className="block font-semibold">Satış raporunun gerçek tarihi</label>
        <p className="mt-1 text-xs">Dosyayı bugün yüklemiş olsan bile satışın gerçekleştiği tarihi seç. Geriye dönük raporlar için gerçek rapor tarihini gir.</p>
        <input id="nebim-sales-report-date" data-testid="nebim-sales-report-date" type="date" value={reportDate} onChange={(event) => onReportDateChange(event.target.value)} className="mt-2 w-full max-w-xs rounded-md border border-border bg-white px-3 py-2 text-sm text-foreground" required />
        {!reportDate ? <p role="alert" className="mt-2 font-medium">Gerçek rapor tarihi seçilmeden içe aktarma yapılamaz.</p> : <p className="mt-2">Kaydedilecek rapor tarihi: <strong>{new Date(reportDate + "T12:00:00").toLocaleDateString("tr-TR")}</strong></p>}
      </section>

      {/* Dosya + hash */}
      <section className="rounded-lg border border-border bg-card p-4 text-xs">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
          <div>
            <span className="text-muted-foreground">Dosya: </span>
            <span className="font-mono text-foreground">
              {parseResult.file_name}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">SHA-256: </span>
            <span className="font-mono text-foreground">
              {parseResult.file_hash.slice(0, 16)}…
            </span>
          </div>
        </div>
      </section>

      {/* Zorunlu sütun eksikse engel */}
      {parseResult.missing_required.length > 0 && (
        <div
          data-testid="nebim-missing-columns"
          className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 text-destructive" />
            <div>
              <p className="font-semibold text-foreground">
                Zorunlu sütunlar eksik
              </p>
              <p className="text-muted-foreground">
                İçe aktarma başlatılamaz. Eksik kanonik alanlar:{" "}
                <span className="font-mono text-xs">
                  {parseResult.missing_required.join(", ")}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      
      {parseResult.row_count === 0 ? (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          <strong>Kaydedilebilir satır bulunamadı.</strong> Boş bir aktarım oluşturulamaz. Doğru Nebim satış raporunu seçip yeniden deneyin.
        </div>
      ) : null}

      {/* Eşlenmeyen ürünler */}
      {productsChecked && unmatchedSkus.length > 0 && (
        <div
          data-testid="nebim-unmatched"
          className="rounded-lg border border-accent/40 bg-accent/5 p-4 text-sm"
        >
          <p className="font-semibold text-foreground">
            {unmatchedSkus.length} ürün sistemde eşleşmedi
          </p>
          <p className="mb-2 text-xs text-muted-foreground">
            Bu SKU'lar <span className="font-mono">products</span> tablosunda
            bulunmadı. Bu ön kontrol yalnızca ürün kodunu karşılaştırır; barkod,
            renk ve beden varyantı için kesin eşleştirme yapmaz. Satırlar yalnızca
            staging'e kaydedilir; gerçek ürün/stok/satış kaydı oluşturulmaz.
          </p>
          <div className="max-h-28 overflow-y-auto font-mono text-[11px] text-foreground">
            {unmatchedSkus.slice(0, 30).join(", ")}
            {unmatchedSkus.length > 30 ? `  …+${unmatchedSkus.length - 30}` : ""}
          </div>
        </div>
      )}
      {!productsChecked && productsError && (
        <div className="rounded-lg border border-border bg-card p-3 text-xs text-muted-foreground">
          Ürün eşleştirme kontrolü yapılamadı: {productsError}
        </div>
      )}

      {/* Satır hatası özeti */}
      {issueGroups.length > 0 && (
        <section
          data-testid="nebim-issues"
          className="rounded-lg border border-border bg-card"
        >
          <div className="border-b border-border px-5 py-3">
            <h3 className="font-serif text-lg text-foreground">
              Satır sorunları
            </h3>
          </div>
          <ul className="divide-y divide-border text-sm">
            {issueGroups.map((g) => (
              <li
                key={g.code}
                className="flex items-start justify-between gap-4 px-5 py-3"
              >
                <div>
                  <div className="font-medium text-foreground">{g.message}</div>
                  <div className="text-xs text-muted-foreground">
                    kod: <span className="font-mono">{g.code}</span>
                  </div>
                </div>
                <div className="shrink-0">
                  <span
                    className={cn(
                      "rounded-full border px-2 py-0.5 text-[11px]",
                      g.severity === "error"
                        ? "border-destructive/40 bg-destructive/10 text-destructive"
                        : "border-accent/40 bg-accent/10 text-accent",
                    )}
                  >
                    {g.count} satır
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* İlk 10 satır örneği */}
      <section
        data-testid="nebim-preview-table"
        className="overflow-x-auto rounded-lg border border-border bg-card"
      >
        <div className="border-b border-border px-5 py-3">
          <h3 className="font-serif text-lg text-foreground">
            İlk 10 satır örneği
          </h3>
          {parseResult.header_row_index != null ? (
            <p className="text-xs text-muted-foreground">
              Başlık satırı Excel'de {parseResult.header_row_index}. satırda bulundu.
            </p>
          ) : null}
        </div>
        <table className="w-full text-xs">
          <thead className="bg-secondary/50 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Durum</th>
              <th className="px-3 py-2">Ürün Kodu</th>
              <th className="px-3 py-2">Ürün Adı</th>
              <th className="px-3 py-2">Renk</th>
              <th className="px-3 py-2">Beden</th>
              <th className="px-3 py-2 text-right">Brüt Mik.</th>
              <th className="px-3 py-2 text-right">İade</th>
              <th className="px-3 py-2 text-right">Net Mik.</th>
              <th className="px-3 py-2 text-right">Satış (VH)</th>
              <th className="px-3 py-2 text-right">Satış (VD)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {previewRows.map((r) => (
              <tr key={r.row_index}>
                <td className="px-3 py-1.5 font-mono">{r.row_index}</td>
                <td className="px-3 py-1.5">
                  <StatusChip status={r.status} />
                </td>
                <td className="px-3 py-1.5 font-mono">{r.parsed.sku ?? "—"}</td>
                <td className="px-3 py-1.5">
                  {r.parsed.product_name ?? "—"}
                </td>
                <td className="px-3 py-1.5">
                  {r.parsed.color_code ?? "—"}
                </td>
                <td className="px-3 py-1.5">{r.parsed.size ?? "—"}</td>
                <td className="px-3 py-1.5 text-right font-mono">
                  {formatNumber(r.parsed.gross_qty)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono">
                  {formatNumber(r.parsed.return_qty)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono">
                  {formatNumber(r.parsed.net_qty)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono">
                  {formatNumber(r.parsed.net_sales_vh)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono">
                  {formatNumber(r.parsed.net_sales_vd)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          onClick={onCancel}
          data-testid="nebim-preview-cancel"
        >
          <X className="mr-1.5 h-4 w-4" />
          İptal
        </Button>
        <Button
          onClick={onConfirm}
          disabled={parseResult.missing_required.length > 0 || parseResult.row_count === 0 || !reportDate}
          data-testid="nebim-preview-confirm"
        >
          <CheckCircle2 className="mr-1.5 h-4 w-4" />
          Onayla ve içe aktar ({parseResult.row_count} satır)
        </Button>
      </div>
    </div>
  );
};

const SuccessCard: React.FC<{
  result: Extract<CommitResult, { ok: true }>;
  onReset: () => void;
}> = ({ result, onReset }) => (
  <div
    data-testid="nebim-done"
    className="space-y-4 rounded-lg border border-border bg-card p-6"
  >
    <div className="flex items-start gap-3">
      <CheckCircle2 className="mt-0.5 h-6 w-6 text-accent" />
      <div>
        <h3 className="font-serif text-2xl text-foreground">
          İçe aktarma kaydı tamamlandı
        </h3>
        <p className="text-sm text-muted-foreground">
          Staging satırları kalıcı içe aktarma kayıtlarına yazıldı. Kanonik satış tablosuna işlenme durumu aşağıda ayrı olarak gösterilir. Hatalı satırlar{" "}
          <span className="font-mono">data_issues</span> tablosunda saklanıyor.
        </p>
      </div>
    </div>
    {result.canonicalization ? (
      <div className={cn("rounded-md border p-4 text-sm", result.canonicalization.materialized ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5")}>
        <strong>{result.canonicalization.materialized ? "Kanonik satış kaydı işlendi." : "Günlük satış tablosuna işlenmedi."}</strong>
        <p className="mt-1">{result.canonicalization.message ?? (
          result.canonicalization.materialized
            ? `${result.canonicalization.canonical_rows ?? result.row_count} kaynak satır, ${result.canonicalization.source_date ?? "kaynak tarih"} ile kanonik satış tablosunda.`
            : result.canonicalization.mode === "period_aggregate_only"
              ? `Kaynak dönem ${result.canonicalization.source_period_start ?? "?"} – ${result.canonicalization.source_period_end ?? "?"}. Çok günlük ürün toplamı günlük tarihlere bölünmedi.`
              : "Kaynak tarih doğrulanamadığı için satış satırları staging üzerinde korundu; günlük ciro uydurulmadı."
        )}</p>
      </div>
    ) : null}
    <dl className="grid grid-cols-2 gap-3 rounded-md border border-border p-4 text-sm sm:grid-cols-4">
      <div>
        <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Batch ID
        </dt>
        <dd className="font-mono text-xs text-foreground">
          {result.batch_id.slice(0, 8)}…
        </dd>
      </div>
      <div>
        <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Toplam
        </dt>
        <dd className="font-mono text-foreground">{result.row_count}</dd>
      </div>
      <div>
        <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Geçerli
        </dt>
        <dd className="font-mono text-foreground">{result.valid_count}</dd>
      </div>
      <div>
        <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Hatalı
        </dt>
        <dd className="font-mono text-foreground">{result.error_count}</dd>
      </div>
    </dl>
    <Button variant="outline" onClick={onReset} data-testid="nebim-done-reset">
      Yeni dosya içe aktar
    </Button>
  </div>
);

const ErrorCard: React.FC<{
  message: string | null;
  onReset: () => void;
}> = ({ message, onReset }) => (
  <div
    data-testid="nebim-error"
    className="space-y-3 rounded-lg border border-destructive/40 bg-destructive/5 p-5"
  >
    <div className="flex items-start gap-3">
      <AlertCircle className="mt-0.5 h-5 w-5 text-destructive" />
      <div className="space-y-1">
        <h3 className="font-serif text-xl text-foreground">
          İçe aktarma başarısız
        </h3>
        <p className="text-sm text-muted-foreground">
          {message ?? "Bilinmeyen hata."}
        </p>
      </div>
    </div>
    <Button variant="outline" onClick={onReset} data-testid="nebim-error-reset">
      Yeniden dene
    </Button>
  </div>
);

const Counter: React.FC<{
  label: string;
  value: number;
  tone?: "success" | "warn" | "error";
  testId: string;
}> = ({ label, value, tone, testId }) => (
  <div
    data-testid={testId}
    className="rounded-lg border border-border bg-card p-4"
  >
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
      {label}
    </div>
    <div
      className={cn(
        "num mt-1 text-2xl font-500",
        tone === "success"
          ? "text-foreground"
          : tone === "warn"
            ? "text-accent"
            : tone === "error"
              ? "text-destructive"
              : "text-foreground",
      )}
    >
      {value}
    </div>
  </div>
);

const StatusChip: React.FC<{ status: "ok" | "warn" | "error" }> = ({
  status,
}) => (
  <span
    className={cn(
      "rounded-full border px-2 py-0.5 text-[10px] uppercase",
      status === "ok"
        ? "border-border text-muted-foreground"
        : status === "warn"
          ? "border-accent/40 bg-accent/10 text-accent"
          : "border-destructive/40 bg-destructive/10 text-destructive",
    )}
  >
    {status === "ok" ? "ok" : status === "warn" ? "uyarı" : "hata"}
  </span>
);

function aggregateIssues(parseResult: ParseResult) {
  const map = new Map<
    string,
    { code: string; message: string; severity: "error" | "warning"; count: number }
  >();
  for (const r of parseResult.rows) {
    for (const i of r.issues) {
      const key = i.code;
      const existing = map.get(key);
      if (existing) existing.count += 1;
      else map.set(key, { ...i, count: 1 });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

function formatBatchStatus(status: string): string {
  const labels: Record<string, string> = {
    parsing: "Okunuyor", parsed: "Okundu", validating: "Doğrulanıyor",
    validated: "Doğrulandı", previewed: "Önizleme hazır", approved: "Onaylandı",
    committing: "Kaydediliyor", committed: "Staging'e kaydedildi",
    failed: "Başarısız", superseded: "Yerine yenisi geçti",
  };
  return labels[status] ?? status;
}

function formatNumber(n: number | null): string {
  if (n == null) return "—";
  return n.toLocaleString("tr-TR", {
    maximumFractionDigits: 2,
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
  });
}